import { Box, Button, Snackbar } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { CommandListEntry } from "../../model/CommandEntry";
import debounce from "lodash.debounce";

interface CommandListProps {
  fetch?: (take: Number) => void;
  commands: Array<CommandListEntry>;
  totalCount: number;
  fetchSize?: number;
}

const CommandList: React.FC<CommandListProps> = ({
  fetch,
  commands,
  totalCount,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copyMessage, setCopyMessage] = useState("");
  const [openToast, setOpenToast] = useState(false);

  const containerRef = useRef<any>(null);

  const loadedCount = commands.length; // Assuming all commands are loaded

  // Handle scroll events to detect bottom
  const handleScroll = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    // Check if scrolled to bottom (with a small buffer)
    if (scrollHeight - scrollTop - clientHeight < 50 && commands.length < totalCount) {
      // Load more items if we haven't loaded all items yet
      handleFetch(commands.length);
    }
  };

  const handleFetch = debounce((take: Number) => {
    if (fetch) {
      fetch(take);
    }
  }, 150);

  // Handle keyboard navigation
  const handleKeyDown = (e: any) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => {
        const newIndex = prev < commands.length - 1 ? prev + 1 : prev;

        // If approaching the end of visible items, load more
        if (newIndex >= loadedCount - 5 && loadedCount < commands.length) {
          //   fetch((prev) => Math.min(prev + PageSize, commands.length));
        }

        return newIndex;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "c" && (e.ctrlKey || e.metaKey)) {
      // Handle both Ctrl+C (Windows/Linux) and Command+C (Mac)
      copyToClipboard(commands[selectedIndex].command.toString());
    }
  };

  // Copy command to clipboard
  const copyToClipboard = (text: string) => {
    console.log("Attempting to copy text:", text);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log("Text successfully copied to clipboard");
        setOpenToast(true);
        setCopyMessage("Copied!");
      })
      .catch((err) => {
        console.error("Failed to copy text to clipboard:", err);
        console.log("Error details:", {
          message: err.message,
          name: err.name,
          stack: err.stack,
        });
        setOpenToast(true);
        setCopyMessage("Failed to copy");
      });
  };

  useEffect(() => {
    if (containerRef.current) {
      const selectedElement = containerRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedIndex]);

  const handleCloseToast = () => {
    setOpenToast(false);
    setCopyMessage("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        padding: "1rem",
        marginLeft: "1.5rem",
        flexDirection: "column",
        width: "100%",
        maxWidth: "42rem",
      }}
    >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "40rem",
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <Box
        sx={{
          marginBottom: "0.5rem",
          fontSize: "0.875rem",
          lineHeight: "1.25rem",
          color: "#4B5563",
        }}
      >
        Use ↑/↓ arrows to navigate, Ctrl+C (Windows/Linux) or ⌘+C (Mac) to copy
      </Box>
      <Box
        sx={{
          overflowY: "scroll",
        }}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {commands.length > 0 ? (
          commands.map((command, index) => (
            <Box
              key={index}
              sx={{
                padding: "0.75rem",
                borderBottomWidth: "1px",
                borderColor: "#F3F4F6",
                borderStyle: "solid",
                borderWidth: "1px",
                cursor: "pointer",
                display: "flex",
                flexWrap: "wrap",
                ":hover": { backgroundColor: "#F9FAFB" },
                justifyContent: "space-between",
                backgroundColor: selectedIndex === index ? "#DBEAFE" : "",
              }}
              data-index={index}
              onClick={() => setSelectedIndex(index)}
              onDoubleClick={() => copyToClipboard(command.command.toString())}
            >
              <Box>{command.command}</Box>
              <Box sx={{ fontSize: "0.75rem", fontStyle: "italic" }}>
                {command.lastModified}
              </Box>
            </Box>
          ))
        ) : (
          <Box sx={{ padding: "1rem", textAlign: "center", color: "#6B7280" }}>
            No commands match your search
          </Box>
        )}

        {loadedCount < commands.length && (
          <Box
            sx={{
              padding: "0.75rem",
              textAlign: "center",
              color: "#6B7280",
            }}
          >
            Scroll down to load more...
          </Box>
        )}
      </Box>
      </Box>

      <Box
        sx={{
          marginTop: "0.5rem",
          fontSize: "0.875rem",
          lineHeight: "1.25rem",
          color: "#6B7280",
        }}
      >
        Showing {loadedCount} of {totalCount} commands
      </Box>
      <Button
        variant="outlined"
        size="small"
        sx={{ marginTop: "0.5rem", alignSelf: "flex-start" }}
        onClick={() => {
          const allCommands = commands.map((c) => c.command).join("\n");
          navigator.clipboard.writeText(allCommands).then(() => {
            setOpenToast(true);
            setCopyMessage(`${commands.length} commands copied!`);
          });
        }}
      >
        Copier tout
      </Button>
      <Snackbar
        open={openToast}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        message={copyMessage}
      />
    </Box>
  );
};

export default CommandList;
